import React, { useCallback, useState } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import AddToArgumentItem from 'routes/BudgetModule/components/BudgetProposal/Resource/Analyse/AddToArgumentItem';
import ModalTemplate from 'ui/ModalTemplate/ModalTemplate';
import { ArgumentT } from 'routes/BudgetModule/config/types';
import { CreateArgumentButton } from 'routes/BudgetModule/components/BudgetProposal/Resource/Arguments/ArgumentList/CreateArgumentButton';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createArgument } from '../Arguments/hooks/arguments';

type AddToArgumentModalProps = {
    show: boolean;
    closeModal: () => void;
    argumentaires: ArgumentT[];
    addChartToArgument: (argument: ArgumentT) => void;
    resourceSoftwareId: string;
    budgetProposalId: string;
};

const AddToArgumentModal = ({
    show,
    closeModal,
    addChartToArgument,
    argumentaires,
    resourceSoftwareId,
    budgetProposalId,
}: AddToArgumentModalProps) => {
    const { t } = useTranslation();

    /* Handle the modal submit */
    const [selection, setSelection] = useState<number[]>([]);
    const queryClient = useQueryClient();
    const submit = () => {
        selection.forEach((id) => {
            const argument = argumentaires.find(
                (argumentaire) => argumentaire.id === id,
            );
            if (argument !== undefined) {
                addChartToArgument(argument);
            }
        });
        closeModal();
    };

    const createArgumentMutation = useMutation({
        mutationFn: () =>
            createArgument({
                budgetProposalId,
                resourceSoftwareId,
                title: t('budgetProposal.newArgument'),
            }),

        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['arguments', resourceSoftwareId],
            });
        },
    });

    /* Handle the selection inside the modal. We useCallback to avoid reference issues */
    const changeSelection = useCallback(
        (id: number) => {
            let newSelection;
            if (selection.includes(id)) {
                newSelection = selection.filter((s) => s !== id);
            } else {
                newSelection = [...selection, id];
            }
            setSelection(newSelection);
        },
        [selection],
    );

    const argumentModalTitle = _.isEmpty(argumentaires)
        ? t('budgetProposal.createArgumentIfEmpty')
        : t('budgetProposal.selectArgumentOnWhichToAddChart');

    return (
        <ModalTemplate
            modalTitle={argumentModalTitle}
            openModal={show}
            onAction={submit}
            onCloseModal={closeModal}
            actionText={t('common.add')}
            disableActionButton={_.isEmpty(selection)}
        >
            {!_.isEmpty(argumentaires) &&
                argumentaires.map((argument) => (
                    <AddToArgumentItem
                        key={argument.id}
                        argumentaire={argument}
                        isSelected={selection.includes(argument.id)}
                        changeSelection={changeSelection}
                    />
                ))}
            {_.isEmpty(argumentaires) && (
                <CreateArgumentButton
                    onClick={createArgumentMutation.mutate}
                    disabled={createArgumentMutation.isPending}
                />
            )}
        </ModalTemplate>
    );
};

export default AddToArgumentModal;
